/**
 * Sales Check-In only: stamps deal-acceptance/visibility invalidation fields and the dealer's
 * sales-view mode onto the outgoing save-participants request fired by the customer-card activity
 * (`@makemydeal/dr-activities-customer-card`).
 * The activity itself is out of scope, so this rewrites the RSAA body in flight instead of changing
 * how the activity builds it. Must run ahead of `apiMiddleware` in `initialMiddleware.ts` — once
 * `apiMiddleware` sees the action, the HTTP request has already been issued with the original body.
 */

// externals
import isEmpty from 'lodash.isempty';
import { RSAA } from 'redux-api-middleware';
import { Actions, getDealVisibility } from '@makemydeal/dr-sales-checkin-store';
import type { SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';
import { SAVE_PARTICIPANTS_ENDPOINT_PATTERN } from '../../../constants';
import type { ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';

export const saveParticipantsPayloadMiddleware =
    (store: ReduxStore<SalesCheckinRootState>) => (next: ReduxNext) => (action: any) => {
        const rsaa = action?.[RSAA];
        if (!rsaa || rsaa.method !== 'POST' || !SAVE_PARTICIPANTS_ENDPOINT_PATTERN.test(rsaa.endpoint ?? '')) {
            return next(action);
        }

        const state = store.getState();
        const existingVisibility = getDealVisibility(state);
        const visibility =
            existingVisibility && !isEmpty(existingVisibility)
                ? { ...existingVisibility, approvedByManager: false, isDraftDeal: false, needsManagerApproval: true }
                : undefined;
        if (visibility) {
            store.dispatch({ type: Actions.UPDATE_ACTIVE_DEAL_VISIBILITY_STATUS, payload: { visibility } });
        }
        const acceptedByBuyer = false;
        store.dispatch({ type: Actions.SET_DEAL_ACCEPTED_BY_BUYER, payload: { acceptedByBuyer } });

        // The BFF resolves the response's applicationState off the capabilities this mode maps to,
        // the same way the deal save does — without it the resolution is fail-closed to BASE.
        const salesViewMode = state.dealer?.dealerSettings?.salesViewMode;

        return next({
            ...action,
            [RSAA]: {
                ...rsaa,
                body: JSON.stringify({
                    ...JSON.parse(rsaa.body),
                    acceptedByBuyer,
                    ...(visibility && { visibility }),
                    ...(salesViewMode && { salesViewMode })
                })
            }
        });
    };
